import { useTranslation } from 'react-i18next';
import type { UseFormReturn, UseFieldArrayReturn } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from '@/components/molecules/form-field';
import { PriceConverter } from '@/components/molecules/price-converter';
import type { CurrencyType } from '@/lib/utils/price.utils';
import type { ShippingRateFormData } from '../types/shipping-form.types';
import { SHIPPING_WEIGHT_UNITS } from '../types/shipping-form.types';

interface ShippingRateWeightRangesProps {
  form: UseFormReturn<ShippingRateFormData>;
  fieldArray: UseFieldArrayReturn<ShippingRateFormData, 'weightRanges'>;
}

export function ShippingRateWeightRanges({ form, fieldArray }: ShippingRateWeightRangesProps) {
  const { t } = useTranslation();
  const { fields, append, remove } = fieldArray;
  const watchCurrency = form.watch('currency');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shipping.weightConditions', { defaultValue: 'Weight Conditions' })}</CardTitle>
        <CardDescription>
          {t('shipping.weightConditionsDesc', { defaultValue: 'Specify weight ranges and shipping price for each.' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="weightUnit"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shipping.weightUnit', { defaultValue: 'Weight Unit' })}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SHIPPING_WEIGHT_UNITS.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        {fields.map((field, index) => (
          <div key={field.id} className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 items-end p-3 border rounded-lg">
            <FormField
              control={form.control}
              name={`weightRanges.${index}.minWeight`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">{t('shipping.minWeight', { defaultValue: 'Min' })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`weightRanges.${index}.maxWeight`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">{t('shipping.maxWeight', { defaultValue: 'Max' })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`weightRanges.${index}.rate`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">{t('shipping.rate', { defaultValue: 'Rate' })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(index)}
              disabled={fields.length === 1}
              className="shrink-0"
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>

            {(form.watch(`weightRanges.${index}.rate`) ?? 0) > 0 && watchCurrency && (
              <div className="col-span-2 sm:col-span-4">
                <PriceConverter
                  price={form.watch(`weightRanges.${index}.rate`) ?? 0}
                  sourceCurrency={watchCurrency as CurrencyType}
                  compact
                />
              </div>
            )}
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={() => append({ minWeight: 0, maxWeight: 0, rate: 0 })}
          className="w-full"
        >
          <Plus className="size-4 mr-2" />
          {t('shipping.addRange', { defaultValue: 'Add Range' })}
        </Button>
      </CardContent>
    </Card>
  );
}
